/**
 * MeetingIntelligenceCard — read-only Conversation Intelligence surface (FR-CIA-005).
 * See docs/phase2/conversation-intelligence-architecture.md §8.
 *
 * Fetches the latest per-project meeting-intelligence result and renders a read-only
 * card. There is NO upload / retrigger / edit / delete control in the POC — results are
 * produced only by the S3-triggered pipeline. On a null result OR any fetch error the
 * card shows an "Analysis pending" placeholder (no error UI), per §8.
 */

import { useQuery } from "@tanstack/react-query";
import { Brain, CheckCircle2, XCircle } from "lucide-react";

import { useApiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import { phaseLabel } from "@/lib/phases";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// ─── Types (mirror packages/shared/types/conversation-intelligence.ts §7.1) ───
// TODO: import from @kiro-governance/shared once workspace alias configured; types duplicated
// intentionally until then (the frontend is a standalone vite workspace with no path/dependency
// to packages/shared today — tsconfig `paths` only maps `@/*`).

export type SentimentSignal = "positive" | "neutral" | "negative" | "at_risk";

export interface MeetingIntelligenceResult {
  id: number;
  project_id: string;
  s3_key: string;
  meeting_name?: string | null;
  phase: string | null;
  sentiment: SentimentSignal;
  confidence: number;
  topics_covered: string[];
  topics_missing: string[];
  key_observations: string[];
  analysis_version: string;
  used_ltm: boolean;
  analyzed_at: string; // ISO 8601
}

export interface MeetingIntelligenceResponse {
  result: MeetingIntelligenceResult | null;
}

export interface MeetingIntelligenceCardProps {
  projectId: string;
}

// ─── Sentiment presentation (text label + colour — WCAG 2.1 AA: never colour alone) ──

interface SentimentDisplay {
  label: string;
  variant: BadgeVariant;
}

const SENTIMENT_DISPLAY: Record<SentimentSignal, SentimentDisplay> = {
  positive: { label: "Positive", variant: "success" }, // green
  neutral: { label: "Neutral", variant: "neutral" }, // grey
  negative: { label: "Negative", variant: "warning" }, // orange
  at_risk: { label: "At Risk", variant: "danger" }, // red
};

function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
}

// ─── Sub-sections ─────────────────────────────────────────────────────────────

function TopicList({
  topics,
  emptyLabel,
  tone,
}: {
  topics: string[];
  emptyLabel: string;
  tone: "covered" | "missing";
}): JSX.Element {
  if (topics.length === 0) {
    return <p className="text-sm text-text-secondary">{emptyLabel}</p>;
  }

  const Icon = tone === "covered" ? CheckCircle2 : XCircle;
  const iconClass = tone === "covered" ? "text-emerald-600" : "text-amber-600";

  return (
    <ul className="space-y-1.5">
      {topics.map((topic) => (
        <li
          key={topic}
          className="flex items-start gap-2 text-sm text-text-primary"
        >
          <Icon
            className={cn("mt-0.5 h-4 w-4 shrink-0", iconClass)}
            aria-hidden="true"
          />
          <span>{topic}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Card shell (shared across all render states) ──────────────────────────────

function CardShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <Brain className="h-4 w-4 text-primary" aria-hidden="true" />
          Meeting Intelligence
        </h3>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function MeetingIntelligenceCard({
  projectId,
}: MeetingIntelligenceCardProps): JSX.Element {
  const client = useApiClient();

  const query = useQuery({
    queryKey: ["meeting-intelligence", projectId],
    queryFn: async (): Promise<MeetingIntelligenceResponse> => {
      // Best-effort fetch: suppress the global error toast — on error the card degrades
      // to "Analysis pending" (§8), it is not an error state the user must act on.
      const response = await client.get<MeetingIntelligenceResponse>(
        `/api/projects/${projectId}/meeting-intelligence/latest`,
        { _suppressErrorToast: true },
      );
      return response.data;
    },
    enabled: Boolean(projectId),
    retry: false,
  });

  // State (a) — loading.
  if (query.isLoading) {
    return (
      <CardShell>
        <div
          className="space-y-3"
          role="status"
          aria-label="Loading meeting intelligence"
        >
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </CardShell>
    );
  }

  const result = query.data?.result ?? null;

  // State (c) — null result OR any fetch error: "Analysis pending" placeholder (no error UI).
  if (query.isError || result === null) {
    return (
      <CardShell>
        <p className="text-sm text-text-secondary">Analysis pending</p>
      </CardShell>
    );
  }

  // State (b) — full result.
  const sentiment = SENTIMENT_DISPLAY[result.sentiment];
  const confidencePct = `${Math.round(result.confidence * 100)}%`;

  return (
    <CardShell>
      <div className="space-y-5">
        {/* Meeting title */}
        {result.meeting_name && (
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-medium text-text-secondary">From:</span>
            <span className="font-semibold text-text-primary">
              {result.meeting_name}
            </span>
          </div>
        )}

        {/* Sentiment + confidence */}
        <div className="flex flex-wrap items-center gap-3">
          <Badge
            variant={sentiment.variant}
            data-testid="sentiment-badge"
            data-sentiment={result.sentiment}
          >
            {sentiment.label}
          </Badge>
          <span className="text-sm text-text-secondary">
            Confidence:{" "}
            <span className="font-medium text-text-primary">
              {confidencePct}
            </span>
          </span>
        </div>

        {/* Topics covered */}
        <section aria-labelledby="cia-topics-covered">
          <h4
            id="cia-topics-covered"
            className="mb-1.5 text-sm font-semibold text-text-primary"
          >
            Topics Covered
          </h4>
          <TopicList
            topics={result.topics_covered}
            emptyLabel="None identified"
            tone="covered"
          />
        </section>

        {/* Topics missing */}
        <section aria-labelledby="cia-topics-missing">
          <h4
            id="cia-topics-missing"
            className="mb-1.5 text-sm font-semibold text-text-primary"
          >
            Topics Missing
          </h4>
          <TopicList
            topics={result.topics_missing}
            emptyLabel="All expected topics covered"
            tone="missing"
          />
        </section>

        {/* Key observations (omit section entirely when empty) */}
        {result.key_observations.length > 0 && (
          <section aria-labelledby="cia-observations">
            <h4
              id="cia-observations"
              className="mb-1.5 text-sm font-semibold text-text-primary"
            >
              Key Observations
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-text-primary">
              {result.key_observations.map((observation) => (
                <li key={observation}>{observation}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Metadata */}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-outlines pt-3 text-xs">
          {result.meeting_name && (
            <div>
              <dt className="font-medium uppercase tracking-wide text-text-secondary">
                Meeting
              </dt>
              <dd className="mt-0.5 text-text-primary">
                {result.meeting_name}
              </dd>
            </div>
          )}
          {result.phase !== null && (
            <div>
              <dt className="font-medium uppercase tracking-wide text-text-secondary">
                Phase
              </dt>
              <dd className="mt-0.5 text-text-primary">
                {phaseLabel(result.phase)}
              </dd>
            </div>
          )}
          <div>
            <dt className="font-medium uppercase tracking-wide text-text-secondary">
              Analyzed at
            </dt>
            <dd className="mt-0.5 text-text-primary">
              {formatTimestamp(result.analyzed_at)}
            </dd>
          </div>
        </dl>

        {/* LTM note */}
        {!result.used_ltm && (
          <p className="text-xs italic text-text-secondary">
            Note: analysed without project context (LTM not configured)
          </p>
        )}
      </div>
    </CardShell>
  );
}

export default MeetingIntelligenceCard;
